// Third party
import React from 'react';
import { connect } from 'react-redux';
import { withFormik, FormikProps, Form, Field } from 'formik';
import * as Yup from 'yup';

// Components
import { Button } from '@capintel/component-lib/button';
import { FormInput } from '../forms';
import { CancelButton } from '../buttons';

// Utils
import { formatMessage, FormattedMessage, LOCALE_OPTIONS as localeOptions } from '../../../utils/i18n';
import { showError } from '../../../utils/errors';
import notification from '../../../utils/notification';

// Actions
import { ReduxState } from '../../../redux/modules/types';
import { saveContact } from '../../../redux/modules/contacts';
import { CONTACT_LAYOUT, getContactLayout } from '../../../utils/contacts';
import GenericContact from '@/types/generic-contact';
import User from '@/types/user';
import { SelectInput, SelectOption } from '../inputs';
import Locale from '@/types/locale';
import { PERMISSIONS, userHasPermission } from '@/utils/permissions';
import { PROVINCE_KEYS } from '@/utils/location';

type DispatchProps = {
  saveContact: (contact: Partial<GenericContact>) => Promise<GenericContact>;
};

type StateProps = {
  contact: Partial<GenericContact>;
};

type FormValues = {
  uid: string;
  fname: string;
  lname: string;
  email: string;
  locale: Locale;
  province?: string;
  contactType?: string;
  note?: string;
};

type ParentProps = {
  user: User;
  onSave: (contact: GenericContact) => void;
  onCancel: () => void;
  isEditing?: boolean;
  saveLabelId?: string;
};

type Props = DispatchProps & ParentProps & StateProps & FormikProps<FormValues>;

const EditContact = (props: Props) => {
  const { fname, lname, locale, contactType, province } = props.values;
  const { isSubmitting } = props;

  const saveIsDisabled = () => {
    const hasContactLastName = userHasPermission(props.user, PERMISSIONS.CONTACTS_LAST_NAME);
    const hasContactProvince = userHasPermission(props.user, PERMISSIONS.CONTACTS_PROVINCE);
    return Boolean(!fname || (hasContactLastName && !lname) || (hasContactProvince && !province) || !locale || isSubmitting);
  };

  const onCancel = () => {
    props.onCancel();
  };

  const filteredLocales = localeOptions.filter(localeOption => props.user.appConfig.appLocales.includes(localeOption.value));
  const onLocaleChange = (value: any) => {
    props.setFieldValue('locale', value);
  };

  const provinces = PROVINCE_KEYS.map(key => ({
    key,
    value: key,
    label: formatMessage(`Provinces.${key}`),
  }));
  const onProvinceChange = (value: any) => {
    props.setFieldValue('province', value);
  };

  const onAccoutTypeChange = (value: any) => {
    props.setFieldValue('contactType', value);
  };

  const hasContactLocale = userHasPermission(props.user, PERMISSIONS.CONTACTS_LOCALE);
  const canEditLocale = hasContactLocale && props.user.appConfig.appLocales.length > 1;
  const hasContactEmail = userHasPermission(props.user, PERMISSIONS.CONTACTS_EMAIL);
  const hasContactLastName = userHasPermission(props.user, PERMISSIONS.CONTACTS_LAST_NAME);
  const hasContactProvince = userHasPermission(props.user, PERMISSIONS.CONTACTS_PROVINCE);
  const hasContactNotes = userHasPermission(props.user, PERMISSIONS.CONTACTS_NOTES);

  let availableContactTypes: string[] = [];
  if (userHasPermission(props.user, PERMISSIONS.CONTACTS_TYPE)) {
    availableContactTypes = ['individual', 'joint', 'corporate'];
  }

  return (
    <div className="edit-contact">
      <FormattedMessage tagName="h1" id={`EditContact.${props.contact._id && props.isEditing ? 'EditTitle' : 'CreateTitle'}`} />
      <Form>
        <div className="container">
          {availableContactTypes.length > 0 && (
            <div className="row" data-private data-dd-privacy="mask">
              <label>
                <FormattedMessage id="Contacts.Fields.ContactType" />*
              </label>
              <SelectInput onValueChange={onAccoutTypeChange} selected={contactType}>
                {availableContactTypes.map(contactType => (
                  <SelectOption key={contactType} value={contactType}>
                    <FormattedMessage id={`Contacts.ContactTypes.${contactType}`} />
                  </SelectOption>
                ))}
              </SelectInput>
            </div>
          )}
          <div className="row" data-private data-dd-privacy="mask">
            <FormInput className="col" name="uid" label={formatMessage('Contacts.Fields.ContactID')} placeholder={formatMessage('EditContact.UniqueId')} />
          </div>

          {!hasContactLastName && (
            <div className="row" data-private data-dd-privacy="mask">
              <FormInput className="col" name="fname" label={formatMessage('Contacts.Fields.FirstName')} placeholder="David" required />
            </div>
          )}

          {hasContactLastName && (
            <div className="row" data-private data-dd-privacy="mask">
              <FormInput className="col" name="fname" label={formatMessage('Contacts.Fields.FirstName')} placeholder="David" required />
              <FormInput className="col" name="lname" label={formatMessage('Contacts.Fields.LastName')} placeholder="Doe" required />
            </div>
          )}

          {hasContactEmail && (
            <div className="row" data-private data-dd-privacy="mask">
              <FormInput
                className="col"
                name="email"
                label={formatMessage('Contacts.Fields.Email')}
                placeholder={formatMessage('Contacts.ContactDetailsInfo.Placeholders.Email')}
              />
            </div>
          )}

          {canEditLocale && (
            <div className="row" data-private data-dd-privacy="mask">
              <label>
                <FormattedMessage id="Contacts.Fields.Locale" />*
              </label>
              <SelectInput onValueChange={onLocaleChange} selected={locale}>
                {filteredLocales.map(localeOption => (
                  <SelectOption key={localeOption.value} value={localeOption.value}>
                    {localeOption.label}
                  </SelectOption>
                ))}
              </SelectInput>
            </div>
          )}

          {hasContactProvince && (
            <div className="row" data-private data-dd-privacy="mask">
              <label>
                <FormattedMessage id="Contacts.Fields.Province" />*
              </label>
              <SelectInput onValueChange={onProvinceChange} selected={province}>
                {provinces.map(province => (
                  <SelectOption key={province.value} value={province.value}>
                    {province.label}
                  </SelectOption>
                ))}
              </SelectInput>
            </div>
          )}

          {hasContactNotes && (
            <div className="row" data-private data-dd-privacy="mask">
              <label>
                <FormattedMessage id="Contacts.Fields.Notes" />
              </label>
              <Field component="textarea" className="contact-note col" name="note" placeholder={formatMessage('EditContact.Optional')} />
            </div>
          )}
        </div>
        <div className="edit-contact-actions">
          <CancelButton onClick={onCancel} />

          <Button variant="primary" disabled={saveIsDisabled()} type="submit">
            <FormattedMessage id={props.saveLabelId ?? 'Contacts.Save'} />
          </Button>
        </div>
      </Form>
    </div>
  );
};

const FormikWrapper = withFormik<Props, FormValues>({
  validationSchema: (props: Props) =>
    Yup.object().shape({
      uid: Yup.string(),
      fname: Yup.string().required(),
      lname: Yup.string(),
      email: Yup.string(),
      locale: Yup.string().required(),
      contactType: userHasPermission(props.user, PERMISSIONS.CONTACTS_TYPE) ? Yup.string().required() : Yup.string(),
      province: userHasPermission(props.user, PERMISSIONS.CONTACTS_PROVINCE) ? Yup.string().required() : Yup.string(),
      note: Yup.string(),
    }),
  mapPropsToValues: (initialValues: Props) => {
    const defaultLocale = initialValues.user.authUser.locale || initialValues.user.appConfig.appLocales[0];
    const types = ['individual'];
    const defaultContactType = types != null && types.length > 0 ? types[0] : undefined;
    return {
      uid: initialValues.isEditing ? initialValues.contact.uid || '' : '',
      fname: initialValues.isEditing ? initialValues.contact.fname || '' : '',
      lname: initialValues.isEditing ? initialValues.contact.lname || '' : '',
      email: initialValues.isEditing ? initialValues.contact.email || '' : '',
      locale: initialValues.isEditing ? initialValues.contact.locale || defaultLocale : defaultLocale,
      contactType: initialValues.isEditing ? initialValues.contact.contactType || defaultContactType : defaultContactType,
      note: initialValues.isEditing ? initialValues.contact.note || '' : '',
      province: initialValues.isEditing ? initialValues.contact.province || undefined : undefined,
    };
  },
  handleSubmit: (values, formikBag) => {
    const contact: Partial<GenericContact> = { ...values, _id: formikBag.props.contact._id };

    formikBag.props
      .saveContact(contact)
      .then((c: GenericContact) => {
        formikBag.props.onSave(c);

        const contactLayout = getContactLayout(formikBag.props.user);

        if (contactLayout === CONTACT_LAYOUT.CLIENT) {
          notification.success(formatMessage(`EditContact.${contact._id ? 'EditSuccessClient' : 'CreateSuccessClient'}`));
        } else {
          notification.success(formatMessage(`EditContact.${contact._id ? 'EditSuccess' : 'CreateSuccess'}`));
        }
      })
      .catch(showError)
      .finally(() => {
        formikBag.setSubmitting(false);
      });
  },
})(EditContact);

const mapStateToProps = (state: ReduxState) => ({
  contact: state.contacts.editContact,
});

export default connect(mapStateToProps, {
  saveContact,
})(FormikWrapper) as any;
